/**
 * Size guide modal.
 *
 * Opened by any [data-size-guide-trigger] on the page — the variant picker
 * renders one beside the size option. Listening on document rather than on the
 * triggers themselves keeps it working after Dawn swaps the variant picker
 * markup on a variant change.
 */
class SizeGuideModal extends HTMLElement {
  static NUMERIC = /\d+(?:\.\d+)?/g;

  constructor() {
    super();

    this.baseUnit = this.dataset.baseUnit === 'cm' ? 'cm' : 'in';
    this.unit = this.baseUnit;
    this.openedBy = null;

    this.dialog = this.querySelector('.size-guide__dialog');
    this.closeButton = this.querySelector('[data-size-guide-close]');
    this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));
    this.cells = Array.from(this.querySelectorAll('[data-size-guide-measure]'));

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.closeButton?.addEventListener('click', () => this.close());
    this.querySelector('[data-size-guide-overlay]')?.addEventListener('click', () => this.close());

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.sizeGuideUnit));
    });

    // The authored values carry no unit suffix; render them with one up front.
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onDocumentClick(event) {
    const trigger = event.target?.closest?.('[data-size-guide-trigger]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.close();
  }

  open(trigger = null) {
    this.openedBy = trigger;
    this.hidden = false;
    document.body.classList.add('overflow-hidden');

    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, this.closeButton || this.dialog);
    } else {
      this.closeButton?.focus();
    }
  }

  close() {
    if (this.hidden) return;

    this.hidden = true;
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.openedBy);
    } else {
      this.openedBy?.focus();
    }

    this.openedBy = null;
  }

  setUnit(unit) {
    if (unit !== 'cm' && unit !== 'in') return;

    this.unit = unit;
    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeGuideUnit === unit));
    });

    this.render();
  }

  render() {
    this.cells.forEach((cell) => {
      cell.textContent = this.format(cell.dataset.sizeGuideMeasure);
    });
  }

  /**
   * Converts every number inside a cell, so ranges like "38-40" survive intact,
   * and leaves cells without digits alone.
   */
  format(raw) {
    const value = (raw || '').trim();
    if (!/\d/.test(value)) return value;

    let text = value;

    if (this.unit !== this.baseUnit) {
      const factor = this.unit === 'cm' ? 2.54 : 1 / 2.54;
      text = value.replace(SizeGuideModal.NUMERIC, (number) => this.round(parseFloat(number) * factor));
    }

    return this.unit === 'cm' ? `${text} cm` : `${text}"`;
  }

  /** Whole centimeters, half inches — the granularity each unit is read at. */
  round(number) {
    return String(this.unit === 'cm' ? Math.round(number) : Math.round(number * 2) / 2);
  }
}

if (!customElements.get('size-guide-modal')) {
  customElements.define('size-guide-modal', SizeGuideModal);
}

/* Theme editor: surface the modal while the merchant is editing it. */
document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelector('size-guide-modal')?.open();
});

document.addEventListener('shopify:section:deselect', (event) => {
  event.target.querySelector('size-guide-modal')?.close();
});

document.addEventListener('shopify:block:select', (event) => {
  event.target.closest('size-guide-modal')?.open();
});
